import React, { forwardRef, useState, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Colors,
    registerables
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Box, Button, ButtonGroup, Typography, Tooltip as MuiTooltip } from "@mui/material";
import { useTheme } from "@emotion/react";
import { scoreLevels } from "../../context/global";
import { prettifyNumber } from "../../functions/Formatters";

// Custom plugin for phase group labels
const phaseGroupLabelsPlugin = {
    id: 'phaseGroupLabels',
    afterDraw: (chart, args, options) => {
        const phaseGroups = options.phaseGroups;
        
        if (!phaseGroups || phaseGroups.length === 0) {
            return;
        }
        
        const ctx = chart.ctx;
        const xAxis = chart.scales.x;
        
        if (!xAxis || !chart.chartArea) {
            return;
        }
        
        ctx.save();
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        
        phaseGroups.forEach((group, index) => {
            try {
                // Calculate center position for the phase label
                const startX = xAxis.getPixelForValue(group.startIndex);
                const endX = xAxis.getPixelForValue(group.endIndex);
                const centerX = (startX + endX) / 2;
                const y = chart.chartArea.bottom + 25;
                
                // Draw phase name text
                ctx.fillStyle = '#333';
                ctx.fillText(group.name, centerX, y);
                
                // Draw separator line before this phase (except for first phase)
                if (index > 0) {
                    const separatorX = startX - (xAxis.getPixelForValue(1) - xAxis.getPixelForValue(0)) / 2;
                    ctx.strokeStyle = '#ccc';
                    ctx.lineWidth = 2;
                    ctx.setLineDash([]);
                    ctx.beginPath();
                    ctx.moveTo(separatorX, chart.chartArea.bottom + 10);
                    ctx.lineTo(separatorX, chart.chartArea.bottom + 40);
                    ctx.stroke();
                }
            } catch (error) {
                console.error('Error drawing phase group:', error, group);
            }
        });
        
        ctx.restore();
    }
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Colors,
    ...registerables
);

// Register the phase group labels plugin
ChartJS.register(phaseGroupLabelsPlugin);

const ProgressGraph = forwardRef(({ project }, forwardedRef) => {
    const styling = useTheme();
    const flatKpis = useSelector((state) => state.themes.flatKpis);
    
    // Create internal ref
    const internalRef = useRef(null);
    // Use forwarded ref if provided, otherwise use internal ref
    const chartRef = forwardedRef || internalRef;

    const kpisInProject = useMemo(() => {
        const kpiSet = new Set();
        
        project.phases.forEach((phase) => {
            phase.loops.forEach((loop) => {
                loop.loop_kpis.filter((loopKpi) => loopKpi.is_selected).forEach((loopKpi) => {
                    kpiSet.add(loopKpi.kpi);
                    
                });
            });
        });
        return Array.from(kpiSet);
    }, [project]);


    // Helper function to find KPI score in design
    const findKpiScoreInDesign = (design, kpiId) => {
        if (!design.scores) return null;
        const score = design.scores.find(score => score.kpi === kpiId);
        if (!score) return null;
        if (score.value === null) return null;

        if (typeof score.value === "number" || typeof score.value === "string") {
            return Number(score.value);
        }
        const totalValue = score.value.reduce((acc, segment) => {
            return (
            acc +
            segment.values.reduce((acc, layer) => {
                acc += Number(layer.value) || 0;
                return acc;
            }, 0)
            );
        }, 0);
        return totalValue;      
    };

    // Helper functions for chart configuration
    const getChartTitle = (data) => {
        if (data?.currentKpi) {
            return `${data.currentKpi.name} voortgang over ontwerploops`;
        }
        return 'Voortgang over ontwerploops';
    };

    const getYAxisTitle = (data) => {
        if (data?.currentKpi) {
            const unit = data.currentKpi.unit ? `(${data.currentKpi.unit})` : '';
            return `${data.currentKpi.name} ${unit}`;
        }
        return 'Score';
    };

    // Get all available KPIs from project themes
    const availableKpis = useMemo(() => {
        const kpis = kpisInProject.map(kpi => flatKpis[kpi]);
        // Add cost KPI
        kpis.push({
            id: "cost",
            name: "Kosten",
            description: "Kosten van het ontwerp",
            unit: "€",
            scale_type: -1,
            maximize: false,
        });
        return kpis;
    }, [flatKpis]);

    const [selectedKpi, setSelectedKpi] = useState(availableKpis.length > 0 ? availableKpis[0].id : null);

    // Process design data across all loops
    const processedData = useMemo(() => {
        if (!selectedKpi || !project.phases) return null;

        const currentKpi = availableKpis.find(k => k.id === selectedKpi);
        if (!currentKpi) return null;

        const loops = [];
        const loopLabels = [];
        

        // Collect all loops from all phases and track phase grouping
        const phaseGroups = [];
        project.phases.forEach((phase, phaseIndex) => {
            const phaseStartIndex = loops.length;
            let phaseLoopCount = 0;
            
            phase.loops.forEach((loop, loopIndex) => {
                if (loop.designs && loop.designs.length > 0) {
                    loops.push({
                        ...loop,
                        name: `Ronde ${loopIndex + 1}`,
                        designs: loop.designs,
                        isReference: false,
                        phaseIndex: phaseIndex,
                        phaseName: phase.phase_name
                    });
                    loopLabels.push(`Ronde ${loopIndex + 1}`); // Shortened labels
                    phaseLoopCount++;
                }
            });
            
            // Add phase group info if this phase has loops
            if (phaseLoopCount > 0) {
                phaseGroups.push({
                    name: phase.phase_name,
                    startIndex: phaseStartIndex,
                    endIndex: phaseStartIndex + phaseLoopCount - 1,
                    loopCount: phaseLoopCount
                });
            }
        });

        if (loops.length === 0) return null;

        // Extract scores for each loop
        const minScores = [];
        const maxScores = [];
        const goalValues = [];
        const allDesignScores = [];

        loops.forEach((loop) => {
            const loopScores = [];
            
            loop.designs.forEach((design) => {
                let score = null;
                
                if (selectedKpi === "cost") {
                    // Handle cost scores
                    score = design.investment_cost || 0;
                } else {
                    // Handle regular KPI scores
                    score = findKpiScoreInDesign(design, currentKpi.id);
                }
                
                if (score !== null) {
                    loopScores.push(score);
                }
            });

            // Calculate min/max for this loop
            if (loopScores.length > 0) {
                minScores.push(Math.min(...loopScores));
                maxScores.push(Math.max(...loopScores));
                allDesignScores.push(loopScores.filter((ls) => ls != Math.min(...loopScores) & ls != Math.max(...loopScores)));
            } else {
                minScores.push(null);
                maxScores.push(null);
                allDesignScores.push([]);
            }

            // Get goal value for this kpi for this loop and add to goalValues
            const goals = loop.loop_kpis.filter(loopKpi => loopKpi.is_selected).find(loopKpi => loopKpi.kpi === currentKpi.id)?.loop_goals;
            const goalValue = goals && goals.length > 0 ? goals[0].value : null;            
            goalValues.push(goalValue);
        });

        return {
            labels: loopLabels,
            minScores,
            maxScores,
            goalValues,
            allDesignScores,
            currentKpi,
            phaseGroups
        };
    }, [selectedKpi, project, availableKpis]);

    // Create chart data
    const chartData = useMemo(() => {
        if (!processedData) return null;

        const datasets = [];

        // Goal line
        if (processedData.goalValues.some(v => v !== null)) {
            datasets.push({
                label: `Doel ${processedData.currentKpi.maximize ? "(min)" : "(max)"}`,
                data: processedData.goalValues,
                borderColor: "#FF0000",
                backgroundColor: "#FFFFFF",
                borderWidth: 2,
                borderDash: [8, 8],
                pointStyle: false,
                pointRadius: 6,
                fill: false,
                type: 'line'
            });
        }

        // Min scores line
        datasets.push({
            label: "Laagste score",
            data: processedData.minScores,
            borderColor: styling.palette.error.main,
            backgroundColor: 'rgba(200, 200, 200, 0.5)',
            pointBackgroundColor: styling.palette.error.main,
            borderWidth: 2,
            fill: false,
            pointRadius: 4,
            type: 'line',
        })

        // Max scores line with fill to min scores
        datasets.push({
            label: "Hoogste score",
            data: processedData.maxScores,
            borderColor: styling.palette.success.main,
            backgroundColor: 'rgba(200, 200, 200, 0.5)',
            pointBackgroundColor: styling.palette.success.main,
            borderWidth: 2,
            fill: '-1', // Fill to previous dataset (min scores)
            pointRadius: 4,
            type: 'line'
        });

        // Individual design scores as scatter points
        processedData.allDesignScores.forEach((loopScores, loopIndex) => {
            loopScores.forEach((score, scoreIndex) => {
                datasets.push({
                    label: `Design ${scoreIndex + 1} (${processedData.labels[loopIndex]})`,
                    data: processedData.labels.map((_, i) => i === loopIndex ? score : null),
                    borderColor: 'black',
                    borderWidth: 2,
                    backgroundColor: styling.palette.primary.grey,
                    pointRadius: 4,
                    showLine: false,
                    type: 'scatter'
                });
            });
        });

        return {
            labels: processedData.labels,
            datasets
        };
    }, [processedData, styling]);

    const options = {
        responsive: true,
        layout: {
            padding: {
                bottom: 50 // Extra space for phase labels
            }
        },

        plugins: {
            phaseGroupLabels: {
                phaseGroups: processedData?.phaseGroups || []
            },
            title: {
                display: true,
                text: getChartTitle(processedData),
                font: {
                    size: 16,
                },
            },
            legend: {
                display: true,
                position: 'top',
                labels: {
                    // filter: function(legendItem) {
                    //     // Only show main lines and goal, hide individual design points
                    //     return ['Doel (min)', 'Doel (max)', 'Laagste score', 'Hoogste score'].includes(legendItem.text);
                    // },
                    generateLabels: function(chart) {
                        // Get default labels
                        const original = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
                        
                        // Filter to only show main items
                        let filtered = original.filter(item => 
                            ['Doel (min)', 'Doel (max)', 'Laagste score', 'Hoogste score'].includes(item.text)
                        ).map((item) => ({...item, fillStyle: item?.lineDash?.length > 0 ? item.fillStyle : item.strokeStyle}))
                        
                        // Add custom "Andere ontwerpen" legend item
                        filtered.push({
                            text: 'Andere ontwerpen',
                            fillStyle: 'rgba(200, 200, 200, 0.5)', // Grey content
                            strokeStyle: '#000000', // Black border
                            lineWidth: 2,
                            hidden: false,
                            index: filtered.length,
                            // Make it look like a small bar with point
                            pointStyle: 'circle',
                            datasetIndex: -1 // Not associated with any dataset
                        });
                        
                        return filtered;
                    }
                }
            },
            tooltip: {
                filter: function(tooltipItem) {
                    return tooltipItem.parsed.y !== null;
                },
                callbacks: {
                    title: function(tooltipItems) {
                        return tooltipItems[0].label;
                    },
                    label: function(tooltipItem) {
                        const value = tooltipItem.parsed.y;
                        const unit = processedData?.currentKpi?.unit || '';
                        const formattedValue = prettifyNumber(value, unit);
                        return `${tooltipItem.dataset.label}: ${formattedValue}`;
                    }
                }
            }
        },
        scales: {
            x: {
                title: {
                    display: false,
                    text: 'Ontwerprondes'
                }
            },
            y: {
                title: {
                    display: true,
                    text: getYAxisTitle(processedData)
                },
                beginAtZero: true,
                ticks: {
                    callback: function(value) {
                        if (processedData?.currentKpi?.scale_type === 0) {
                            if (value === 0) {
                                return 'Geen score';
                            }
                            if (Number.isInteger(value)) {
                                return scoreLevels[value -1]?.label || '';
                            } else {
                                return ''
                            }

                        } else {
                            return prettifyNumber(value, '')
                        }
                    },
                    stepSize: 1,
                    
                }
            }
        },
        interaction: {
            intersect: false,
            mode: 'point'
        }
    };

    const handleKpiChange = (kpiId) => {
        setSelectedKpi(kpiId);
    };

    if (availableKpis.length === 0) {
        return (
            <Box>
                <Typography variant="h6" gutterBottom>
                    Voortgangsgrafiek
                </Typography>
                <Typography color="textSecondary">
                    Er zijn nog geen KPI's gedefinieerd voor dit project.
                </Typography>
            </Box>
        );
    }

    if (!chartData) {
        return (
            <Box>
                <Typography variant="h6" gutterBottom>
                    Voortgangsgrafiek
                </Typography>
                <Typography color="textSecondary">
                    Er zijn nog geen ontwerpen om weer te geven.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Voortgangsgrafiek
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
                Deze grafiek toont de voortgang van ontwerp scores over alle ontwerprondes. 
                De lijnen tonen de laagste en hoogste scores per ronde, terwijl individuele ontwerpen als punten worden weergegeven.
            </Typography>

            {/* KPI Selection */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1">Selecteer KPI:</Typography>
                <ButtonGroup variant="outlined" size="small" sx={{ flexWrap: 'wrap' }}>
                    {availableKpis.map((kpi) => (
                        <MuiTooltip key={kpi.id} title={kpi.description || `${kpi.name}${kpi.unit ? ` (${kpi.unit})` : ''}`} enterDelay={1000}>
                            <Button
                                onClick={() => handleKpiChange(kpi.id)}
                                variant={selectedKpi === kpi.id ? "contained" : "outlined"}
                                sx={{
                                    minWidth: 120,
                                    textTransform: 'none',
                                    fontWeight: selectedKpi === kpi.id ? 'bold' : 'normal'
                                }}
                            >
                                <Typography variant="body2">
                                    {kpi.name}{kpi.unit && ` (${kpi.unit})`}
                                </Typography>
                            </Button>
                        </MuiTooltip>
                    ))}
                </ButtonGroup>
            </Box>

            {/* Chart */}
            <Box sx={{ height: 400, width: '100%' }}>
                <Line 
                    ref={chartRef} 
                    data={chartData} 
                    options={options}
                />
            </Box>
        </Box>
    );
});

ProgressGraph.displayName = 'ProgressGraph';

ProgressGraph.propTypes = {
    project: PropTypes.object.isRequired,
};

export default ProgressGraph;
